import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  X,
  Search,
  Calendar,
} from 'lucide-react';
import { Column } from '.';
import { useSearchParams } from 'react-router-dom';

interface TableHeaderProps<T> {
  columns: Column[];
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;
  onRowUpdate?: (updatedRow: T) => void;
  onFilter?: (columnKey: string, value: string) => void;
  selectable?: boolean;
  allRowsSelected?: boolean;
  someRowsSelected?: boolean;
  onToggleSelectAll?: () => void;
}

export const TableHeader = <T extends object>({
  columns,
  sortColumn,
  sortDirection,
  handleSort,
  onFilter,
  selectable = false,
  allRowsSelected = false,
  someRowsSelected = false,
  onToggleSelectAll,
}: TableHeaderProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState<Record<string, boolean>>({});
  const [textInputValues, setTextInputValues] = useState<
    Record<string, string>
  >({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someRowsSelected && !allRowsSelected;
    }
  }, [allRowsSelected, someRowsSelected]);

  useEffect(() => {
    const updatedTextInputs: Record<string, string> = {};
    columns.forEach((column) => {
      const paramValue = searchParams.get(column.accessorKey);
      updatedTextInputs[column.accessorKey] = paramValue ?? '';
    });
    setTextInputValues(updatedTextInputs);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const clickedOutsideAll = Object.entries(dropdownRefs.current).every(
        ([, ref]) => !ref || !ref.contains(e.target as Node),
      );
      if (clickedOutsideAll) setShowFilter({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDateColumn = (columnKey: string) => {
    const column = columns.find((col) => col.accessorKey === columnKey);
    return column?.header.toLowerCase().includes('date');
  };

  const toggleFilter = (columnKey: string) => {
    setShowFilter((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((k) => [k, false])),
      [columnKey]: !prev[columnKey],
    }));
  };

  const preservePaginationParams = (params: URLSearchParams) => {
    const pageNumber = searchParams.get('pageNumber');
    const pageSize = searchParams.get('pageSize');
    if (pageNumber) params.set('pageNumber', pageNumber);
    if (pageSize) params.set('pageSize', pageSize);
    return params;
  };

  const handleFilterChange = (columnKey: string, value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set(columnKey, value);
    } else {
      newSearchParams.delete(columnKey);
    }
    preservePaginationParams(newSearchParams);
    setSearchParams(newSearchParams);
    setTextInputValues((prev) => ({ ...prev, [columnKey]: value }));
    onFilter?.(columnKey, value);
  };

  const handleTextInputChange = (columnKey: string, value: string) => {
    setTextInputValues((prev) => ({ ...prev, [columnKey]: value }));
  };

  const handleTextInputKeyDown = (
    columnKey: string,
    event: React.KeyboardEvent,
  ) => {
    if (event.key === 'Enter') {
      handleFilterChange(columnKey, textInputValues[columnKey] || '');
      setShowFilter((prev) => ({ ...prev, [columnKey]: false }));
    }
    if (event.key === 'Escape') {
      setShowFilter((prev) => ({ ...prev, [columnKey]: false }));
    }
  };

  const clearAllFilters = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    const keysToDelete: string[] = [];
    newSearchParams.forEach((_, key) => {
      if (
        key !== 'pageNumber' &&
        key !== 'pageSize' &&
        key !== 'sortColumn' &&
        key !== 'sortDirection'
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => newSearchParams.delete(key));
    setSearchParams(newSearchParams);

    const clearedInputs: Record<string, string> = {};
    columns.forEach((column) => {
      clearedInputs[column.accessorKey] = '';
    });
    setTextInputValues(clearedInputs);
    columns.forEach((column) => {
      if (column.filterable) onFilter?.(column.accessorKey, '');
    });
    setShowFilter({});
  };

  const clearSingleFilter = (columnKey: string) => {
    handleFilterChange(columnKey, '');
    setShowFilter((prev) => ({ ...prev, [columnKey]: false }));
  };

  const getActiveFilters = () => {
    const filters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (
        key !== 'pageNumber' &&
        key !== 'pageSize' &&
        key !== 'sortColumn' &&
        key !== 'sortDirection'
      ) {
        filters[key] = value;
      }
    });
    return filters;
  };

  const getFilterLabel = (column: Column, value: string): string => {
    if (column.filterOptions) {
      return (
        column.filterOptions.find((o) => o.value === value)?.label ?? value
      );
    }
    return value;
  };

  const renderFilterDropdown = (column: Column) => {
    if (!column.filterable || !showFilter[column.accessorKey]) return null;

    const currentValue = searchParams.get(column.accessorKey) || '';

    return (
      <div
        className="filter-dropdown-enter absolute top-full left-0 mt-2 z-50 min-w-[220px] bg-white rounded-xl overflow-hidden"
        style={{
          boxShadow:
            '0 10px 40px rgba(0,0,0,0.12), 0 2px 10px rgba(0,0,0,0.07)',
          border: '1px solid rgba(0,0,0,0.07)',
        }}
      >
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-gray-50/80 border-b border-gray-100">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {column.header}
          </span>
          {currentValue && (
            <button
              onClick={() => clearSingleFilter(column.accessorKey)}
              className="text-[11px] text-red-400 hover:text-red-600 font-semibold flex items-center gap-1 transition-colors"
            >
              <X size={10} />
              Clear
            </button>
          )}
        </div>

        <div className="p-2.5">
          {column.filterType === 'select' && column.filterOptions ? (
            <div className="space-y-0.5">
              <button
                onClick={() => {
                  handleFilterChange(column.accessorKey, '');
                  setShowFilter((p) => ({ ...p, [column.accessorKey]: false }));
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                  !currentValue
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                All
              </button>
              {column.filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    handleFilterChange(column.accessorKey, option.value);
                    setShowFilter((p) => ({
                      ...p,
                      [column.accessorKey]: false,
                    }));
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between group ${
                    currentValue === option.value
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span>{option.label}</span>
                  {currentValue === option.value && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          ) : column.filterType === 'date' ? (
            <div className="space-y-2.5">
              <div className="relative">
                <Calendar
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="date"
                  value={textInputValues[column.accessorKey] || ''}
                  onChange={(e) =>
                    handleTextInputChange(column.accessorKey, e.target.value)
                  }
                  onKeyDown={(e) =>
                    handleTextInputKeyDown(column.accessorKey, e)
                  }
                  onBlur={(e) => {
                    if (e.target.value)
                      handleFilterChange(column.accessorKey, e.target.value);
                  }}
                  className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white text-gray-700 transition-all"
                />
              </div>
              <button
                onClick={() => {
                  handleFilterChange(
                    column.accessorKey,
                    textInputValues[column.accessorKey] || '',
                  );
                  setShowFilter((p) => ({ ...p, [column.accessorKey]: false }));
                }}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={textInputValues[column.accessorKey] || ''}
                  onChange={(e) =>
                    handleTextInputChange(column.accessorKey, e.target.value)
                  }
                  onKeyDown={(e) =>
                    handleTextInputKeyDown(column.accessorKey, e)
                  }
                  autoFocus
                  className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white text-gray-700 transition-all"
                  placeholder={`Search ${column.header.toLowerCase()}...`}
                />
              </div>
              <p className="text-[11px] text-gray-400 text-center pb-0.5">
                Press Enter to apply
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const activeFilters = getActiveFilters();
  const activeFilterCount = Object.keys(activeFilters).length;

  return (
    <>
      <style>{`
        @keyframes filterFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0px) scale(1); }
        }
        .filter-dropdown-enter {
          animation: filterFadeIn 0.14s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      {/* 👇 sticky: the entire thead sticks to the top of the scroll container */}
      <thead className="sticky top-0 z-20">
        {/* Active filter pills row */}
        {activeFilterCount > 0 && (
          <tr>
            <th
              colSpan={columns.length + 1 + (selectable ? 1 : 0)}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100/80"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <SlidersHorizontal size={12} className="text-blue-500" />
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                    Filters
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap flex-1">
                  {Object.entries(activeFilters).map(([key, value]) => {
                    const col = columns.find((c) => c.accessorKey === key);
                    if (!col) return null;
                    return (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1.5 bg-white border border-blue-200 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full"
                        style={{ boxShadow: '0 1px 3px rgba(59,130,246,0.12)' }}
                      >
                        <span className="text-blue-400 font-normal text-[11px]">
                          {col.header}:
                        </span>
                        <span>{getFilterLabel(col, value)}</span>
                        <button
                          onClick={() => clearSingleFilter(key)}
                          className="text-blue-300 hover:text-blue-600 transition-colors -mr-0.5"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="ml-auto text-[11px] font-semibold text-blue-500 hover:text-blue-700 transition-colors flex items-center gap-1 whitespace-nowrap bg-white border border-blue-200 px-2.5 py-1 rounded-full hover:border-blue-400"
                >
                  <X size={10} />
                  Clear all
                </button>
              </div>
            </th>
          </tr>
        )}

        {/* Column headers row */}
        <tr className="bg-gray-50 border-b-2 border-gray-100">
          {selectable && (
            <th className="px-4 sm:px-5 py-3.5 text-left bg-gray-50">
              <input
                ref={selectAllRef}
                type="checkbox"
                aria-label="Select all visible rows"
                checked={allRowsSelected}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
          )}
          {columns.map((column, index) => {
            const isActive = !!activeFilters[column.accessorKey];
            const isOpen = !!showFilter[column.accessorKey];

            return (
              <th
                key={index}
                className="px-4 sm:px-5 py-3.5 text-left bg-gray-50"
                style={{ minWidth: column.minWidth }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap select-none">
                      {column.header}
                    </span>
                    {isDateColumn(column.accessorKey) && (
                      <button
                        onClick={() => handleSort(column.accessorKey)}
                        className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                      >
                        {sortColumn === column.accessorKey ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp size={13} className="text-blue-500" />
                          ) : (
                            <ChevronDown size={13} className="text-blue-500" />
                          )
                        ) : (
                          <ChevronUp size={13} className="text-gray-300" />
                        )}
                      </button>
                    )}
                  </div>

                  {column.filterable && (
                    <div
                      className="relative flex-shrink-0"
                      ref={(el) => {
                        dropdownRefs.current[column.accessorKey] = el;
                      }}
                    >
                      <button
                        onClick={() => toggleFilter(column.accessorKey)}
                        title={`Filter by ${column.header}`}
                        className={`flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-sm'
                            : isOpen
                              ? 'bg-gray-200 text-gray-700'
                              : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <SlidersHorizontal size={12} />
                        {isActive && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white/90 inline-block" />
                        )}
                      </button>

                      {isOpen && renderFilterDropdown(column)}
                    </div>
                  )}
                </div>
              </th>
            );
          })}

          <th className="px-4 sm:px-5 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50">
            View Details
          </th>
        </tr>
      </thead>
    </>
  );
};
